import { HeartOff, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { localize } from '../i18n/script';
import { favoritesApi, type FavoriteList, type FavoriteState } from '../services/favoritesApi';

import { CategoryThumb } from './picks/PickCard';
import { useSiteActions } from './site/siteActionsContext';
import Card from './ui/Card';

/**
 * "我的收藏" in My Home (PRD §5.9 F4): YOYO activities and YOYO Picks, newest first.
 * Items that ended or were taken down stay listed with a label and can be removed.
 * Rows follow the site-wide saved state, so removing here or anywhere else agrees.
 */
export default function MyFavoritesTab() {
  const { t, i18n } = useTranslation(['dashboard', 'common', 'picks']);
  const { isFavorite, toggleFavorite } = useSiteActions();
  const [data, setData] = useState<FavoriteList | null>(null);
  const [failed, setFailed] = useState(false);
  const locale = i18n.language;

  useEffect(() => {
    let cancelled = false;
    favoritesApi
      .list()
      .then((list) => !cancelled && setData(list))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);

  if (failed) {
    return (
      <Card padding="lg">
        <p className="py-8 text-center text-gray-600">{t('dashboard:favorites.loadError')}</p>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card padding="lg">
        <div className="py-8 text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  const activities = data.activities.filter((a) => isFavorite('activity', a.id));
  const picks = data.picks.filter((p) => isFavorite('pick', p.id));

  const stateBadge = (state: FavoriteState) =>
    state === 'open' ? null : (
      <span className="flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
        {state === 'ended' ? t('dashboard:favorites.ended') : t('dashboard:favorites.unlisted')}
      </span>
    );

  const removeButton = (onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex flex-shrink-0 items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
    >
      <HeartOff className="h-4 w-4" />
      {t('common:favorite.remove')}
    </button>
  );

  if (activities.length === 0 && picks.length === 0) {
    return (
      <Card padding="lg">
        <div className="py-8 text-center">
          <p className="mb-4 text-gray-600">{t('dashboard:favorites.empty')}</p>
          <Link
            to="/"
            className="font-semibold text-primary transition-colors hover:text-primary/80"
          >
            {t('dashboard:favorites.browse')}
          </Link>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {activities.length > 0 && (
        <section>
          <h3 className="mb-3 text-lg font-semibold text-gray-900">
            {t('dashboard:favorites.activities')}
          </h3>
          <div className="space-y-3">
            {activities.map((a) => {
              const title = localize(a.titleCN, a.titleEN, locale);
              const href = a.kind === 'course' ? `/courses/${a.slug}` : `/activity/${a.slug}`;
              return (
                <Card key={a.id} padding="sm">
                  <div className="flex items-center gap-3">
                    <img
                      src={a.imageUrl ?? '/placeholder-activity.jpg'}
                      alt=""
                      className="h-12 w-16 flex-shrink-0 rounded-md object-cover"
                    />
                    {a.state === 'unlisted' ? (
                      <span className="min-w-0 flex-1 truncate font-semibold text-gray-500">
                        {title}
                      </span>
                    ) : (
                      <Link
                        to={href}
                        className="min-w-0 flex-1 truncate font-semibold text-gray-900 hover:text-primary"
                      >
                        {title}
                      </Link>
                    )}
                    {stateBadge(a.state)}
                    {removeButton(() => toggleFavorite('activity', a.id))}
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}
      {picks.length > 0 && (
        <section>
          <h3 className="mb-3 text-lg font-semibold text-gray-900">{t('picks:name')}</h3>
          <div className="space-y-3">
            {picks.map((p) => {
              const title = localize(p.titleCN, p.titleEN, locale);
              return (
                <Card key={p.id} padding="sm">
                  <div className="flex items-center gap-3">
                    <CategoryThumb category={p.category} />
                    {p.state === 'unlisted' ? (
                      <span className="min-w-0 flex-1 truncate font-semibold text-gray-500">
                        {title}
                      </span>
                    ) : (
                      <Link
                        to={`/picks/${p.slug}`}
                        className="min-w-0 flex-1 truncate font-semibold text-gray-900 hover:text-primary"
                      >
                        {title}
                      </Link>
                    )}
                    {stateBadge(p.state)}
                    {removeButton(() => toggleFavorite('pick', p.id))}
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
